import { useQuery } from '@tanstack/react-query';

import { conversationQueryOptions } from '@/lib/api/conversations.js';

/**
 * The workspace an automation's files live in — the one its dedicated chat belongs to, never the one
 * the sidebar happens to be on.
 *
 * They are not the same thing, and the difference is invisible until it is too late: a run resolves its
 * attachments in the workspace of its own conversation, so a file picked from anywhere else comes back
 * empty weeks later, as a note saying the file could not be reached. The automation itself carries no
 * workspace — only the chat it delivers to does — so it is read from there.
 *
 * `workspaceId` is `null` for every case where the answer is not known: an automation with no chat, a
 * chat that does not resolve, a chat with no workspace, and the moment before the read finishes. Most
 * callers only need that single collapsed value — `useAutomationWorkspaceId` below is the shorthand for
 * them. A caller that has to tell "still loading" apart from "resolved to nothing" (a placeholder that
 * shows one message while settling and a different one once genuinely empty) reads `isLoading` too,
 * and `failed` to tell either of those from a read that never answered.
 */
export function useAutomationWorkspace(
  conversationId: string | null | undefined,
): { workspaceId: string | null; isLoading: boolean; failed: boolean } {
  const conversation = useQuery({
    ...conversationQueryOptions(conversationId ?? ''),
    enabled: typeof conversationId === 'string' && conversationId.length > 0,
  });
  return {
    workspaceId: conversation.data?.workspaceId ?? null,
    isLoading: conversation.isLoading,
    failed: conversation.isError,
  };
}

/** Shorthand for the common case: only the collapsed workspace id, never the loading state. */
export function useAutomationWorkspaceId(
  conversationId: string | null | undefined,
): string | null {
  return useAutomationWorkspace(conversationId).workspaceId;
}
